import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("bansal_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    // Only treat a real unauthorized response as logout. Network blips during
    // backend restarts (no response / 502/503) must not wipe the session.
    if (status === 401 && !window.location.pathname.startsWith("/login")) {
      localStorage.removeItem("bansal_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

/** Dedupe identical GETs (StrictMode remount, concurrent hooks). */
const inflightGets = new Map();
const recentGets = new Map();
const GET_DEDUPE_TTL_MS = 500;

function getDedupeKey(url, params) {
  if (!params || typeof params !== "object") return String(url);
  const sorted = Object.keys(params)
    .sort()
    .reduce((acc, key) => {
      acc[key] = params[key];
      return acc;
    }, {});
  return `${url}\0${JSON.stringify(sorted)}`;
}

const axiosGet = api.get.bind(api);
api.get = (url, config = {}) => {
  if (config.skipDedupe) {
    const { skipDedupe: _skip, ...rest } = config;
    return axiosGet(url, rest);
  }
  const key = getDedupeKey(url, config.params);
  const now = Date.now();
  const recent = recentGets.get(key);
  if (recent && recent.expiresAt > now) {
    return recent.promise;
  }
  const existing = inflightGets.get(key);
  if (existing) return existing;
  const promise = axiosGet(url, config)
    .then((result) => {
      const cached = Promise.resolve(result);
      recentGets.set(key, {
        promise: cached,
        expiresAt: Date.now() + GET_DEDUPE_TTL_MS,
      });
      setTimeout(() => {
        const entry = recentGets.get(key);
        if (entry?.promise === cached) recentGets.delete(key);
      }, GET_DEDUPE_TTL_MS);
      return result;
    })
    .catch((err) => {
      inflightGets.delete(key);
      recentGets.delete(key);
      return Promise.reject(err);
    });
  inflightGets.set(key, promise);
  promise.finally(() => {
    inflightGets.delete(key);
  });
  return promise;
};

/** Normalize list API body (paginated object or legacy bare array). */
/** Fetch full list for dropdowns / dashboards (`export=1`). */
export async function fetchAllListResults(path, params = {}) {
  const { data } = await api.get(path, { params: { ...params, export: 1 } });
  return normalizePaginatedList(data).results;
}

export function normalizePaginatedList(data) {
  if (Array.isArray(data)) {
    return {
      results: data,
      count: data.length,
      page: 1,
      page_size: data.length,
      total_pages: 1,
      summary: {},
    };
  }
  return {
    results: data?.results ?? [],
    count: data?.count ?? 0,
    page: data?.page ?? 1,
    page_size: data?.page_size ?? 0,
    total_pages: data?.total_pages ?? 0,
    summary: data?.summary ?? {},
  };
}

export function extractError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Request failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" · ");
  if (d && typeof d === "object") {
    const extra = Array.isArray(d.errors)
      ? d.errors
          .map((e) => {
            const det =
              typeof e?.detail === "string"
                ? e.detail
                : e?.detail?.message || (e?.detail ? JSON.stringify(e.detail) : "");
            return [e?.entity_id != null ? `#${e.entity_id}` : "", det].filter(Boolean).join(" ");
          })
          .filter(Boolean)
      : [];
    const text = [d.message, ...extra].filter(Boolean).join(" · ");
    if (text) return text;
  }
  return JSON.stringify(d);
}

export const LOGO_URL = `${process.env.PUBLIC_URL || ""}/logo.png`;
